<template>
  <div
    class="flex min-w-0 w-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm text-left"
    :class="comfortMode === 'comfortable' ? 'p-6 md:p-8' : ''"
  >
    <!-- Header Row -->
    <div
      class="pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      :class="comfortMode === 'comfortable' ? 'px-2 pt-2 pb-6' : 'px-6 pt-6'"
    >
      <div class="space-y-1">
        <div class="flex items-center gap-2.5">
          <h2
            :class="
              cn(
                'font-black text-slate-900',
                comfortMode === 'comfortable'
                  ? 'text-2xl md:text-3xl'
                  : 'text-xl'
              )
            "
          >
            Needs Your Attention
          </h2>
          <!-- Count badge -->
          <span
            class="inline-flex items-center justify-center rounded-full text-xs font-black h-5.5 px-2 min-w-5.5 transition-colors"
            :class="safeItems.length > 0 ? 'bg-rose-500 text-white' : 'hidden'"
          >
            {{ safeItems.length }}
          </span>
        </div>
        <p
          :class="
            cn(
              'font-semibold',
              comfortMode === 'comfortable'
                ? 'text-sm md:text-base text-slate-600'
                : 'text-xs text-slate-400'
            )
          "
        >
          These items need your review, approval, or feedback.
        </p>
      </div>

      <!-- View All Link (only shown when items exist) -->
      <Button
        v-if="!loading && !error && safeItems.length > 0"
        variant="link"
        :class="
          cn(
            'font-bold text-[#1f7aff] hover:text-[#185adb] p-0 h-auto self-start sm:self-center',
            comfortMode === 'comfortable' ? 'text-sm md:text-base' : 'text-xs'
          )
        "
        @click="handleViewAll"
      >
        View All ({{ safeItems.length }})
      </Button>
    </div>

    <!-- 1. Loading State (Skeletons) -->
    <div
      v-if="loading"
      class="space-y-4"
      :class="comfortMode === 'comfortable' ? 'px-2 pb-2' : 'px-6 pb-6'"
      data-testid="attention-loading-state"
    >
      <!-- Skeletons for filter chips -->
      <div class="flex flex-wrap gap-2.5">
        <Skeleton
          v-for="n in 5"
          :key="`chip-skel-${n}`"
          :class="
            cn(
              'rounded-full bg-slate-100',
              comfortMode === 'comfortable' ? 'h-11 w-24' : 'h-8.5 w-20'
            )
          "
        />
      </div>
      <!-- Skeletons for rows -->
      <div class="space-y-3 pt-2">
        <div
          v-for="n in 3"
          :key="`row-skel-${n}`"
          class="flex items-center justify-between gap-4 p-3 rounded-2xl border border-slate-100/80 bg-slate-50/40"
        >
          <div class="flex items-center gap-3.5 flex-1 min-w-0">
            <Skeleton
              :class="
                cn(
                  'rounded-2xl bg-slate-100 shrink-0',
                  comfortMode === 'comfortable' ? 'h-13 w-13' : 'h-11 w-11'
                )
              "
            />
            <div class="space-y-2 flex-1 min-w-0 max-w-md">
              <Skeleton class="h-4 w-3/4 rounded-md bg-slate-100" />
              <Skeleton class="h-3 w-1/2 rounded-md bg-slate-100" />
            </div>
          </div>
          <Skeleton
            :class="
              cn(
                'rounded-xl bg-slate-100 shrink-0',
                comfortMode === 'comfortable' ? 'h-11 w-24' : 'h-8.5 w-20'
              )
            "
          />
        </div>
      </div>
    </div>

    <!-- 2. Error State -->
    <div
      v-else-if="error"
      class="flex flex-col items-center justify-center text-center rounded-2xl border border-dashed border-rose-200 bg-rose-50/30 p-8 my-2"
      :class="
        comfortMode === 'comfortable' ? 'mx-2 my-2 py-10' : 'mx-6 my-4 py-8'
      "
      data-testid="attention-error-state"
    >
      <div
        class="size-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mb-3"
      >
        <DMSuiteIcon :icon="DASHBOARD_ICONS.warning" :size="24" custom-class="text-rose-600" />
      </div>
      <h3
        :class="
          cn(
            'font-extrabold text-slate-800',
            comfortMode === 'comfortable' ? 'text-lg' : 'text-base'
          )
        "
      >
        Unable to load attention items
      </h3>
      <p
        :class="
          cn(
            'text-slate-500 font-medium max-w-sm mt-1 mb-4',
            comfortMode === 'comfortable' ? 'text-sm' : 'text-xs'
          )
        "
      >
        {{
          typeof error === "string"
            ? error
            : error?.message ||
              "Something went wrong while fetching your attention items. Please try again."
        }}
      </p>
      <Button
        size="sm"
        variant="outline"
        class="rounded-xl border-slate-200 bg-white hover:bg-slate-50 font-bold text-slate-700 flex items-center gap-2"
        @click="$emit('retry')"
      >
        <DMSuiteIcon :icon="DASHBOARD_ICONS.refresh" :size="14" />
        Try Again
      </Button>
    </div>

    <!-- 3. Populated State (Items Exist) -->
    <div v-else-if="safeItems.length > 0">
      <!-- Category Filter Chips -->
      <div
        class="flex flex-wrap gap-2.5 bg-white"
        :class="comfortMode === 'comfortable' ? 'px-2 pb-6' : 'px-6 pb-4'"
      >
        <button
          v-for="chip in categoriesList"
          :key="chip"
          type="button"
          class="inline-flex items-center gap-2 rounded-full font-extrabold transition-all border shrink-0"
          :class="[
            selectedCategory === chip
              ? 'bg-[#1f7aff] text-white border-[#1f7aff] shadow-sm'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900',
            comfortMode === 'comfortable'
              ? 'px-5 py-2.5 text-sm h-11'
              : 'px-4 py-1.5 text-xs h-8.5',
          ]"
          @click="selectedCategory = chip"
        >
          {{ chip }}
          <span
            class="inline-flex items-center justify-center rounded-full font-black"
            :class="[
              selectedCategory === chip
                ? 'bg-[#185adb] text-white'
                : 'bg-slate-100 text-slate-500',
              comfortMode === 'comfortable'
                ? 'text-xs px-2 h-5.5 min-w-5.5'
                : 'text-[10px] px-1.5 min-w-5 h-4.5',
            ]"
          >
            {{ categoryCounts[chip] }}
          </span>
        </button>
      </div>

      <!-- Rows List -->
      <div
        v-if="displayedItems.length > 0"
        class="divide-y divide-slate-100 border-t border-slate-50 bg-white"
        :class="comfortMode === 'comfortable' ? 'px-2' : 'px-6'"
      >
        <article
          v-for="item in displayedItems"
          :key="item.id"
          class="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          :class="comfortMode === 'comfortable' ? 'py-5 md:py-6' : 'py-4'"
        >
          <!-- Left side info -->
          <div class="min-w-0 flex-1 flex items-start gap-4">
            <!-- Pastel Rounded Icon Box -->
            <div
              class="rounded-2xl border flex items-center justify-center shrink-0"
              :class="[
                rowIconColorClass(getItemCategory(item)),
                comfortMode === 'comfortable' ? 'h-13 w-13' : 'h-11 w-11',
              ]"
            >
              <DMSuiteIcon
                :icon="rowIconForCategory(getItemCategory(item))"
                :size="comfortMode === 'comfortable' ? 26 : 22"
              />
            </div>

            <!-- Title & Metadata -->
            <div class="min-w-0 flex-1 space-y-1.5">
              <div class="flex flex-wrap items-center gap-2.5">
                <h4
                  :class="
                    cn(
                      'font-extrabold text-slate-900 leading-snug',
                      comfortMode === 'comfortable'
                        ? 'text-base md:text-lg'
                        : 'text-sm sm:text-base'
                    )
                  "
                >
                  {{ item.title || item.subject }}
                </h4>
              </div>

              <!-- Service & Date Row -->
              <div
                :class="
                  cn(
                    'flex items-center gap-x-2 font-medium',
                    comfortMode === 'comfortable'
                      ? 'text-sm text-slate-500'
                      : 'text-xs text-slate-400'
                  )
                "
              >
                <span class="font-semibold text-slate-700">
                  {{ item.service_name || item.serviceType || "Ad Management" }}
                </span>
                <span class="text-slate-300">•</span>
                <span>
                  Submitted {{ formatDate(item.created_at || item.due_date) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Action Button: right on desktop, stacked on mobile -->
          <div class="shrink-0 self-start sm:self-center w-full sm:w-auto">
            <Button
              size="sm"
              variant="outline"
              :class="
                cn(
                  'w-full p-2 sm:w-auto rounded-xl border-blue-200 bg-white text-[#1f7aff] hover:bg-blue-50/50 hover:text-[#185adb] transition-all font-bold',
                  comfortMode === 'comfortable'
                    ? 'h-11 px-6 text-sm md:text-base'
                    : 'h-8.5 px-5 text-sm'
                )
              "
              @click="$emit('action', item)"
            >
              {{ itemActionLabel(item) }}
            </Button>
          </div>
        </article>
      </div>

      <!-- Filtered Empty State -->
      <div
        v-else
        class="py-12 text-center border-t border-slate-100 bg-slate-50/20"
        :class="comfortMode === 'comfortable' ? 'px-2' : 'px-6'"
      >
        <p
          :class="
            cn(
              'font-semibold text-slate-500',
              comfortMode === 'comfortable' ? 'text-base md:text-lg' : 'text-sm'
            )
          "
        >
          No items found under the selected filter "{{ selectedCategory }}".
        </p>
      </div>
    </div>

    <!-- 4. Modern Empty State (Zero Attention Items) -->
    <div
      v-else
      class="flex flex-col items-center justify-center text-center rounded-2xl border border-slate-100 bg-slate-50/40"
      :class="
        comfortMode === 'comfortable'
          ? 'mx-2 mb-2 py-10 sm:py-12 px-6'
          : 'mx-6 mb-6 py-8 px-4'
      "
      data-testid="attention-empty-state"
    >
      <!-- Soft Blue Circular Icon Wrapper -->
      <div
        class="size-14 sm:size-16 rounded-full bg-blue-50 border border-blue-100 text-[#1f7aff] flex items-center justify-center mb-3.5 shadow-xs shrink-0"
        aria-hidden="true"
      >
        <DMSuiteIcon :icon="DASHBOARD_ICONS.completed" :size="32" custom-class="text-[#1f7aff]" />
      </div>

      <!-- Subtle Status Indicator Pill -->
      <div class="mb-3">
        <span
          class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-emerald-50 border border-emerald-200/70 text-emerald-700 text-xs font-bold"
        >
          <span
            class="size-1.5 rounded-full bg-emerald-500"
            aria-hidden="true"
          />
          All clear
        </span>
      </div>

      <!-- Primary Message -->
      <h3
        :class="
          cn(
            'font-black text-slate-900 tracking-tight',
            comfortMode === 'comfortable'
              ? 'text-lg sm:text-xl'
              : 'text-base sm:text-lg'
          )
        "
      >
        You're all caught up!
      </h3>

      <!-- Supporting Description -->
      <p
        :class="
          cn(
            'font-medium text-slate-500 max-w-md mx-auto mt-1.5 leading-relaxed',
            comfortMode === 'comfortable'
              ? 'text-sm sm:text-base'
              : 'text-xs sm:text-sm'
          )
        "
      >
        No approvals or feedback needed. Any items requiring your review will appear here.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, inject } from "vue";
import { useRouter } from "vue-router";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";
import { DASHBOARD_ICONS } from "@/config/dashboardIcons";

const props = withDefaults(
  defineProps<{
    items?: any[];
    loading?: boolean;
    error?: any;
  }>(),
  {
    items: () => [],
    loading: false,
    error: null,
  }
);

defineEmits<{
  (event: "action", item: any): void;
  (event: "retry"): void;
}>();

const router = useRouter();
const comfortMode = inject<any>("comfortMode", ref("comfortable"));
const selectedCategory = ref("All");
const categoriesList = [
  "All",
  "Approvals",
  "Content",
  "Feedback",
  "Revisions",
  "Other",
] as const;

type CategoryName = (typeof categoriesList)[number];

const safeItems = computed(() =>
  Array.isArray(props.items) ? props.items : []
);

const getItemCategory = (item: any): CategoryName => {
  const title = String(item.title || item.subject || "").toLowerCase();
  const status = String(item.status || "").toLowerCase();

  if (
    title.includes("approve") ||
    title.includes("approval") ||
    status.includes("approval")
  ) {
    return "Approvals";
  }
  if (
    title.includes("content") ||
    title.includes("copy") ||
    title.includes("video") ||
    title.includes("image")
  ) {
    return "Content";
  }
  if (
    title.includes("feedback") ||
    title.includes("comment") ||
    title.includes("input")
  ) {
    return "Feedback";
  }
  if (
    title.includes("revision") ||
    title.includes("change") ||
    title.includes("edit")
  ) {
    return "Revisions";
  }
  return "Other";
};

const categoryCounts = computed(() => {
  const counts: Record<CategoryName, number> = {
    All: safeItems.value.length,
    Approvals: 0,
    Content: 0,
    Feedback: 0,
    Revisions: 0,
    Other: 0,
  };

  safeItems.value.forEach((item) => {
    const cat = getItemCategory(item);
    counts[cat]++;
  });

  return counts;
});

const sortedItems = computed(() => {
  return [...safeItems.value];
});

const filteredAndSortedItems = computed(() => {
  let list = sortedItems.value;

  if (selectedCategory.value !== "All") {
    list = list.filter(
      (item) => getItemCategory(item) === selectedCategory.value
    );
  }

  return list;
});

const displayedItems = computed(() => {
  const limit = comfortMode.value === "comfortable" ? 5 : 8;
  return filteredAndSortedItems.value.slice(0, limit);
});

function rowIconForCategory(cat: string): string {
  if (cat === "Approvals") return DASHBOARD_ICONS.sparkle;
  if (cat === "Content") return DASHBOARD_ICONS.promotionalMaterials;
  if (cat === "Feedback") return DASHBOARD_ICONS.socialMedia;
  if (cat === "Revisions") return DASHBOARD_ICONS.refresh;
  return DASHBOARD_ICONS.needsReview;
}

function rowIconColorClass(cat: string) {
  if (cat === "Approvals")
    return "bg-[#e8f1ff] text-[#2855a6] border-[#bfdbfe]/50";
  if (cat === "Content")
    return "bg-[#fdf2f8] text-[#db2777] border-[#fbcfe8]/50";
  if (cat === "Feedback")
    return "bg-[#fff7ed] text-[#f97316] border-[#fed7aa]/50";
  if (cat === "Revisions")
    return "bg-[#f5f3ff] text-[#7c3aed] border-[#ddd6fe]/50";
  return "bg-[#eff6ff] text-[#2563eb] border-[#bfdbfe]/50";
}

const formatDate = (dateStr?: string | Date | null) => {
  if (!dateStr) return "May 28, 2025";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "May 28, 2025";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const itemActionLabel = (item: any): string => {
  const title = String(item?.title || item?.subject || "").toLowerCase();
  if (title.includes("approve") || title.includes("approval")) return "Approve";
  if (title.includes("feedback") || title.includes("comment")) return "Provide Feedback";
  return "Review";
};

const handleViewAll = () => {
  router.push("/apps/task-dashboard");
};
</script>
